import React, { useState, useEffect } from 'react';
import api from '../../config/axios';
import './MySalary.css';
import Header from '../common/Header';
import { useNavigate } from 'react-router-dom';

interface Salary {
  _id: string;
  staffId: {
    _id: string;
    username: string;
    email: string;
    role: string;
  };
  month?: number;
  year?: number;
  periodIdentifier?: string;
  periodName?: string;
  startDate?: string;
  endDate?: string;
  employmentType?: 'fulltime' | 'parttime';
  baseSalary: number;
  hourlyRate: number;
  effectiveHourlyRate?: number;
  hourlyWage?: number;
  totalHours: number;
  totalShifts: number;
  totalOrders: number;
  totalRevenue: number;
  commission: number;
  bonus: number;
  deductions: number;
  netSalary: number;
  status: 'pending' | 'calculated' | 'paid';
  paidAt?: string;
  notes?: string;
  createdAt: string;
}

interface MySalaryProps {
  user: any;
}

const MySalary: React.FC<MySalaryProps> = ({ user }) => {
  const [salaries, setSalaries] = useState<Salary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSalary, setSelectedSalary] = useState<Salary | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const navigate = useNavigate();

  const fetchMySalaries = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/salaries');
      setSalaries(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Không thể tải thông tin lương');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMySalaries();
  }, []);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Chờ tính';
      case 'calculated': return 'Đã tính';
      case 'paid': return 'Đã thanh toán';
      default: return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'calculated': return 'status-calculated';
      case 'paid': return 'status-paid';
      default: return '';
    }
  };

  const formatCurrency = (amount: number) => {
    return Math.round(amount).toLocaleString('vi-VN') + ' ₫';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const handleViewDetail = (salary: Salary) => {
    setSelectedSalary(salary);
    setShowDetail(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  return (
    <>
      <Header user={user} onLogout={handleLogout}>
        <span className="user-role">Nhân viên</span>
      </Header>
      <div className="my-salary-container">
        <div className="my-salary-header">
          <button
            className="my-salary-back-btn"
            onClick={() => navigate('/')}
            title="Quay lại Dashboard"
          >
            ←
          </button>
          <h2 className="my-salary-title">LƯƠNG CỦA TÔI</h2>
        </div>

        {error && <div className="my-salary-error">{error}</div>}
        {loading && <div className="my-salary-loading">Đang tải thông tin lương...</div>}

        {salaries.length > 0 && (
          <div className="my-salary-stats">
            <div className="my-salary-stat-card">
              <div className="my-salary-stat-icon">💰</div>
              <div className="my-salary-stat-content">
                <h3>Tổng lương đã nhận</h3>
                <p className="my-salary-stat-value">
                  {formatCurrency(salaries
                    .filter(s => s.status === 'paid')
                    .reduce((sum, s) => sum + s.netSalary, 0)
                  )}
                </p>
              </div>
            </div>
            
            <div className="my-salary-stat-card">
              <div className="my-salary-stat-icon">📊</div>
              <div className="my-salary-stat-content">
                <h3>Tháng đã làm việc</h3>
                <p className="my-salary-stat-value">
                  {salaries.filter(s => s.status === 'paid').length} tháng
                </p>
              </div>
            </div>
            
            <div className="my-salary-stat-card">
              <div className="my-salary-stat-icon">⏰</div>
              <div className="my-salary-stat-content">
                <h3>Tổng giờ làm việc</h3>
                <p className="my-salary-stat-value">
                  {salaries.reduce((sum, s) => sum + s.totalHours, 0).toFixed(1)}h
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="my-salary-list-container">
          <h3 className="my-salary-section-title">Lịch sử lương</h3>
          {salaries.length === 0 ? (
            <div className="my-salary-empty">
              <div className="my-salary-empty-icon">💰</div>
              <h3>Chưa có thông tin lương</h3>
              <p>Lương sẽ được tính vào cuối mỗi tháng</p>
            </div>
          ) : (
            <div className="my-salary-list">
              {salaries.map(salary => (
                <div key={salary._id} className="my-salary-card">
                  <div className="my-salary-card-header">
                    <div className="my-salary-period">
                      <span className="my-salary-month">
                        {salary.periodName || 
                         (salary.month && salary.year 
                           ? `Tháng ${salary.month}/${salary.year}`
                           : `${salary.startDate} - ${salary.endDate}`
                         )}
                      </span>
                      <span className={`my-salary-status ${getStatusClass(salary.status)}`}>
                        {getStatusText(salary.status)}
                      </span>
                    </div>
                    <div className="my-salary-amount">
                      {formatCurrency(salary.netSalary)}
                    </div>
                  </div>
                  
                  <div className="my-salary-card-details">
                    <div className="my-salary-detail-item">
                      <span className="my-salary-detail-label">Lương cơ bản:</span>
                      <span className="my-salary-detail-value">{formatCurrency(salary.baseSalary)}</span>
                    </div>
                    <div className="my-salary-detail-item">
                      <span className="my-salary-detail-label">Giờ làm việc:</span>
                      <span className="my-salary-detail-value">{salary.totalHours}h</span>
                    </div>
                    <div className="my-salary-detail-item">
                      <span className="my-salary-detail-label">Đơn hàng:</span>
                      <span className="my-salary-detail-value">{salary.totalOrders} đơn</span>
                    </div>
                    <div className="my-salary-detail-item">
                      <span className="my-salary-detail-label">Hoa hồng:</span>
                      <span className="my-salary-detail-value">{formatCurrency(salary.commission)}</span>
                    </div>
                  </div>
                  
                  <div className="my-salary-card-actions">
                    <button
                      className="my-salary-detail-btn"
                      onClick={() => handleViewDetail(salary)}
                    >
                      Xem chi tiết
                    </button>
                    {salary.status === 'paid' && salary.paidAt && (
                      <span className="my-salary-paid-date">
                        Đã thanh toán: {formatDate(salary.paidAt)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showDetail && selectedSalary && (
          <div className="my-salary-modal">
            <div className="my-salary-modal-content">
              <h3>
                Chi tiết lương {selectedSalary.periodName || 
                 (selectedSalary.month && selectedSalary.year 
                   ? `tháng ${selectedSalary.month}/${selectedSalary.year}`
                   : `từ ${selectedSalary.startDate} đến ${selectedSalary.endDate}`
                 )}
              </h3>
              
              <div className="my-salary-detail-section">
                <h4>Thông tin cơ bản</h4>
                <div className="my-salary-detail-grid">
                  <div className="my-salary-detail-item">
                    <span className="my-salary-detail-label">Hình thức làm việc:</span>
                    <span className="my-salary-detail-value">{selectedSalary.employmentType === 'fulltime' ? 'Fulltime' : 'Parttime'}</span>
                  </div>
                  <div className="my-salary-detail-item">
                    <span className="my-salary-detail-label">{selectedSalary.employmentType === 'fulltime' ? 'Lương cơ bản:' : 'Lương theo giờ:'}</span>
                    <span className="my-salary-detail-value">{selectedSalary.employmentType === 'fulltime' ? formatCurrency(selectedSalary.baseSalary) : `${formatCurrency(selectedSalary.hourlyRate)}/h`}</span>
                  </div>
                  <div className="my-salary-detail-item">
                    <span className="my-salary-detail-label">Đơn giá giờ áp dụng:</span>
                    <span className="my-salary-detail-value">{formatCurrency(selectedSalary.effectiveHourlyRate ?? 0)}/h</span>
                  </div>
                  <div className="my-salary-detail-item">
                    <span className="my-salary-detail-label">Tổng giờ làm:</span>
                    <span className="my-salary-detail-value">{selectedSalary.totalHours}h</span>
                  </div>
                  <div className="my-salary-detail-item">
                    <span className="my-salary-detail-label">Số ca làm việc:</span>
                    <span className="my-salary-detail-value">{selectedSalary.totalShifts} ca</span>
                  </div>
                </div>
              </div>

              <div className="my-salary-detail-section">
                <h4>Hiệu suất làm việc</h4>
                <div className="my-salary-detail-grid">
                  <div className="my-salary-detail-item">
                    <span className="my-salary-detail-label">Tổng đơn hàng:</span>
                    <span className="my-salary-detail-value">{selectedSalary.totalOrders} đơn</span>
                  </div>
                  <div className="my-salary-detail-item">
                    <span className="my-salary-detail-label">Doanh thu tạo ra:</span>
                    <span className="my-salary-detail-value">{formatCurrency(selectedSalary.totalRevenue)}</span>
                  </div>
                  <div className="my-salary-detail-item">
                    <span className="my-salary-detail-label">Hoa hồng:</span>
                    <span className="my-salary-detail-value">{formatCurrency(selectedSalary.commission)}</span>
                  </div>
                  <div className="my-salary-detail-item">
                    <span className="my-salary-detail-label">Thưởng:</span>
                    <span className="my-salary-detail-value">{formatCurrency(selectedSalary.bonus)}</span>
                  </div>
                </div>
              </div>

              <div className="my-salary-detail-section">
                <h4>Tổng kết</h4>
                <div className="my-salary-summary">
                  <div className="my-salary-summary-item">
                    <span className="my-salary-summary-label">
                      Tiền công theo giờ ({selectedSalary.totalHours}h × {formatCurrency(selectedSalary.effectiveHourlyRate ?? 0)}):
                    </span>
                    <span className="my-salary-summary-value">{formatCurrency(selectedSalary.hourlyWage ?? 0)}</span>
                  </div>
                  <div className="my-salary-summary-item">
                    <span className="my-salary-summary-label">Hoa hồng:</span>
                    <span className="my-salary-summary-value">{formatCurrency(selectedSalary.commission)}</span>
                  </div>
                  <div className="my-salary-summary-item">
                    <span className="my-salary-summary-label">Thưởng:</span>
                    <span className="my-salary-summary-value">{formatCurrency(selectedSalary.bonus)}</span>
                  </div>
                  <div className="my-salary-summary-item">
                    <span className="my-salary-summary-label">Khấu trừ:</span>
                    <span className="my-salary-summary-value">-{formatCurrency(selectedSalary.deductions)}</span>
                  </div>
                  <div className="my-salary-summary-item my-salary-total">
                    <span className="my-salary-summary-label">Lương thực nhận:</span>
                    <span className="my-salary-summary-value">{formatCurrency(selectedSalary.netSalary)}</span>
                  </div>
                </div>
              </div>

              {selectedSalary.notes && (
                <div className="my-salary-detail-section">
                  <h4>Ghi chú</h4>
                  <p className="my-salary-notes">{selectedSalary.notes}</p>
                </div>
              )}

              <div className="my-salary-modal-actions">
                <button
                  onClick={() => setShowDetail(false)}
                  className="my-salary-close-btn"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MySalary; 